/**
 * Workarea home — page template.
 *
 * Fired after a new workarea is created (see `contexts/workAreaContext.tsx`
 * `createWorkspaceWorkArea`). Seeds a "Workarea home" page with intro
 * paragraph, welcome callout, two-column section, and a few empty paragraphs
 * for whitespace.
 *
 * Persistence model — every Tiptap node becomes its own Block record. Nesting
 * is expressed via the parent block's `blockIds` array (matching what the
 * editor produces on a fresh save):
 *
 *     page
 *       ├── paragraph                   (intro text)
 *       ├── paragraph                   (empty)
 *       ├── callout (yellow)
 *       │     ├── paragraph             (welcome)
 *       │     ├── paragraph             (empty)
 *       │     ├── paragraph             ("Questions?…")
 *       │     └── bulletList
 *       │           ├── listItem → paragraph (Slack channel:)
 *       │           ├── listItem → paragraph (Email:)
 *       │           └── listItem → paragraph (Team manager:)
 *       ├── paragraph                   (empty)
 *       ├── columnLayout
 *       │     ├── columnItem → heading h2 + horizontalRule + paragraph
 *       │     └── columnItem → heading h2 + horizontalRule + paragraph
 *       └── paragraph                   (empty trailing)
 *
 * Container nodes (callout, bulletList, listItem, columnLayout, columnItem)
 * carry their children's block IDs in `blockIds`; non-container nodes
 * (paragraph, heading, horizontalRule) keep inline text spans in
 * `value.content`. The container list mirrors `CONTAINER_NODE_TYPES` in
 * `utils/blockParser.ts`, which the document reconstructor reads to decide
 * whether to pull children from `blockIds` or from inline JSON.
 */

import { ObjectId } from "bson";
import type { JSONContent } from "@tiptap/core";
import {
  addWorkAreaPageMutation,
  createBlockMutation,
} from "@/indexedDB";
import type { Block, ParentTable } from "@/types/block";

export const WORKAREA_HOME_TEMPLATE_ID = "workarea-home" as const;

/** Yellow callout background — matches the `yellow_background` swatch used
 *  elsewhere (see `utils/formatFormulaValue.ts:107`). */
const CALLOUT_YELLOW = "rgba(251, 243, 219, 0.5)";

/** Node types whose children are stored as separate Block records linked via
 *  `blockIds`. Keep in sync with `CONTAINER_NODE_TYPES` in `utils/blockParser.ts`. */
const CONTAINER_TYPES: ReadonlySet<string> = new Set([
  "bulletList",
  "orderedList",
  "listItem",
  "blockquote",
  "taskList",
  "taskItem",
  "columnLayout",
  "columnItem",
  "table",
  "tableRow",
  "tableCell",
  "tableHeader",
  "callout",
]);

// ─── Tiptap node builders ───────────────────────────────────────────────────

const text = (s: string): JSONContent => ({ type: "text", text: s });

const paragraph = (content?: JSONContent[]): JSONContent => ({
  type: "paragraph",
  ...(content && content.length ? { content } : {}),
});

const heading = (level: 1 | 2 | 3 | 4 | 5 | 6, content: JSONContent[]): JSONContent => ({
  type: "heading",
  attrs: { level },
  content,
});

const horizontalRule = (): JSONContent => ({
  type: "horizontalRule",
});

const bulletList = (rows: JSONContent[][]): JSONContent => ({
  type: "bulletList",
  content: rows.map((inline) => ({
    type: "listItem",
    content: [paragraph(inline)],
  })),
});

const callout = (args: { icon: string; backgroundColor: string; content: JSONContent[] }): JSONContent => ({
  type: "callout",
  attrs: { icon: args.icon, backgroundColor: args.backgroundColor },
  content: args.content,
});

const columnLayout = (count: number, columns: JSONContent[][]): JSONContent => {
  const safeCount = Math.min(Math.max(Math.round(count) || 1, 1), 5);
  const widths = Array.from({ length: safeCount }, () =>
    parseFloat((100 / safeCount).toFixed(2)),
  );
  return {
    type: "columnLayout",
    attrs: { columns: safeCount, widths: widths.join(",") },
    content: widths.map((width, index) => ({
      type: "columnItem",
      attrs: { width },
      content: columns[index]?.length ? columns[index] : [paragraph()],
    })),
  };
};

// ─── Top-level page content ─────────────────────────────────────────────────

function buildWorkareaHomeNodes(): JSONContent[] {
  return [
    // 1. Intro paragraph.
    paragraph([
      text(
        "Give your colleagues a place to learn about your team, and what you’re working on. Use the + buttons in your left-hand sidebar to add more pages, like process docs or a project roadmap.",
      ),
    ]),

    // 2. Empty spacer after intro.
    paragraph(),

    // 3. Welcome callout (yellow background).
    callout({
      icon: "👋",
      backgroundColor: CALLOUT_YELLOW,
      content: [
        paragraph([text("Welcome to the team! Our mission is to…")]),
        paragraph(),
        paragraph([text("Questions? Here’s how to reach us:")]),
        bulletList([
          [text("Slack channel:")],
          [text("Email:")],
          [text("Team manager:")],
        ]),
      ],
    }),

    // 4. Empty spacer after the callout.
    paragraph(),

    // 5. Two-column "About the team" / "Resources" section.
    columnLayout(2, [
      [
        heading(2, [text("About the team")]),
        horizontalRule(),
        paragraph([text("Type the @ key to tag a teammate")]),
      ],
      [
        heading(2, [text("Resources")]),
        horizontalRule(),
        paragraph([text("Type the / key to add custom blocks")]),
      ],
    ]),

    // 6. Trailing empty paragraph.
    paragraph(),
  ];
}

// ─── Block-tree planner ─────────────────────────────────────────────────────

interface PreparedBlock {
  block: Block;
  parentTable: ParentTable;
  /** Sibling that this block follows inside its parent (null = first child). */
  insertAfterBlockID: string | null;
}

interface PlanContext {
  workspaceId: string;
  workareaId: string | null;
}

/**
 * Walk a Tiptap node tree and emit one Block per node. Containers link to
 * their children via `blockIds`; non-containers retain inline text spans in
 * `value.content`. Returned blocks are in top-down DFS order so the
 * persistence loop writes parents before children.
 */
function planBlockTree(
  nodes: JSONContent[],
  rootParentId: string,
  rootParentTable: ParentTable,
  ctx: PlanContext,
): { topLevelIds: string[]; blocks: PreparedBlock[] } {
  const blocks: PreparedBlock[] = [];

  function processNode(
    node: JSONContent,
    parentId: string,
    parentTable: ParentTable,
    insertAfterBlockID: string | null,
  ): string {
    const id = new ObjectId().toString();
    const nodeType = node.type ?? "";
    const isContainer = CONTAINER_TYPES.has(nodeType);
    const rawChildren = Array.isArray(node.content) ? node.content : [];

    // Build this block's value first. Containers drop their inline content
    // (children are reachable via blockIds); non-containers keep it.
    const value: JSONContent = {
      type: nodeType,
      attrs: { ...(node.attrs ?? {}), blockId: id },
    };
    if (!isContainer && rawChildren.length) {
      value.content = rawChildren;
    }

    // Reserve this block's slot in the flat list BEFORE descending, so the
    // parent is persisted before its children.
    const ownIndex = blocks.length;
    const childIds: string[] = [];
    const ownEntry: PreparedBlock = {
      block: {
        _id: id,
        blockType: "content",
        workspaceId: ctx.workspaceId,
        workareaId: ctx.workareaId,
        parentId,
        parentType: parentTable,
        value: value as Block["value"],
        blockIds: childIds,
        status: "alive",
      },
      parentTable,
      insertAfterBlockID,
    };
    blocks.push(ownEntry);

    if (isContainer && rawChildren.length) {
      let previousChildId: string | null = null;
      for (const child of rawChildren) {
        const childId = processNode(child, id, "block", previousChildId);
        childIds.push(childId);
        previousChildId = childId;
      }
    }

    // childIds was mutated in place — block.blockIds points at the same array,
    // so the parent slot reserved at ownIndex now sees the populated list.
    void ownIndex; // explicit silence; the indexed slot was filled via aliasing.
    return id;
  }

  const topLevelIds: string[] = [];
  let prevTopLevel: string | null = null;
  for (const node of nodes) {
    const id = processNode(node, rootParentId, rootParentTable, prevTopLevel);
    topLevelIds.push(id);
    prevTopLevel = id;
  }
  return { topLevelIds, blocks };
}

// ─── Public API ─────────────────────────────────────────────────────────────

export interface CreateWorkareaHomePageArgs {
  workspaceId: string;
  workareaId: string;
  userEmail: string;
  /** Optional override for the page title (default: "Workarea home"). */
  title?: string;
  /** Optional override for the page icon (default: 🏠). */
  icon?: string;
  /** Inject the page + content blocks into React context immediately. */
  addBlockToContext?: (block: Block) => void;
}

export interface CreateWorkareaHomePageResult {
  pageId: string;
  pageBlock: Block;
  contentBlocks: Block[];
}

export async function createWorkareaHomePage(
  args: CreateWorkareaHomePageArgs,
): Promise<CreateWorkareaHomePageResult> {
  const {
    workspaceId,
    workareaId,
    userEmail,
    title = "Workarea home",
    icon = "🏠",
    addBlockToContext,
  } = args;

  const pageId = new ObjectId().toString();
  const nodes = buildWorkareaHomeNodes();

  // One Block per Tiptap node, in top-down persistence order.
  const { topLevelIds, blocks: prepared } = planBlockTree(
    nodes,
    pageId,
    "page",
    { workspaceId, workareaId },
  );

  // Page block sits above the content tree; its blockIds reference the
  // top-level children only — descendants chain through their own parents.
  const pageBlock: Block = {
    _id: pageId,
    blockType: "page",
    workspaceId,
    workareaId,
    parentId: workareaId,
    parentType: "workarea",
    value: {
      title,
      userId: userEmail,
      userEmail,
      icon,
      coverURL: null,
      pageType: "workarea",
      isTemplate: false,
    } as Block["value"],
    blockIds: topLevelIds,
    status: "alive",
  };

  // Optimistic context hydration (caller decides whether to wire this).
  if (addBlockToContext) {
    addBlockToContext(pageBlock);
    for (const { block } of prepared) addBlockToContext(block);
  }

  // Persist the page first, then every content block in document order so
  // each parent exists before its children are written.
  await createBlockMutation({
    block: pageBlock,
    workspaceId,
    workareaId,
    parentId: workareaId,
    parentTable: "workarea",
    insertAfterBlockID: null,
  });

  for (const { block, parentTable, insertAfterBlockID } of prepared) {
    await createBlockMutation({
      block,
      workspaceId,
      workareaId,
      parentId: block.parentId,
      parentTable,
      insertAfterBlockID,
    });
  }

  // Register the page in the workarea's sidebar order. skipSync: true mirrors
  // `app/notes/layout.tsx` handleAddEditor — batch-create API auto-registers
  // root `blockType: "page"` entries, so a separate SIDEBAR_REORDER op would
  // be redundant.
  await addWorkAreaPageMutation(workareaId, pageId, workspaceId, undefined, true);

  return { pageId, pageBlock, contentBlocks: prepared.map((p) => p.block) };
}
